import React, { useCallback, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import Card from '../../../../molecules/Card'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydButton from '../../../../commonDesign/Button'
import Text from '../../../../atoms/Text'
import { ADMIN_ROUTES, TEXT_VARIANTS } from '../../../../../constants'
import { useFormattingContext } from '../../../../organisms/FormattingProvider/FormattingContext'
import { useGetBatchStatus, useListBatchTasks } from '../../../../../api/groups'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import { useGridPageState, useGridSortState } from '../../../../organisms/DenaliAnalysis/tables/operational'
import { StatusBadge, SummaryCardGrid, BatchDetailSkeleton } from '../../shared/BatchComponents'
import BatchTasksTable from './BatchTasksTable'
import { getBatchStatus } from './batchUtils'

function StatusReportBatchDetail () {
  const { batchId: batchIdParam } = useParams()
  const batchId = Number(batchIdParam)
  const history = useHistory()
  const queryClient = useQueryClient()
  const { formatter } = useFormattingContext()
  const [taskFilter, setTaskFilter] = useState('all')
  const { pageState: taskPageState, setPageState: setTaskPageState, resetPageState: resetTaskPageState } = useGridPageState(50)
  const { sortState: taskSortState, setSortState: setTaskSortState } = useGridSortState([{ id: 'batchTaskId', desc: false }])

  const { data, isFetching } = useGetBatchStatus(batchId, {
    refetchInterval: (queryData) => {
      return queryData?.batch?.isProcessing ? 10000 : false
    }
  })

  const batch = data?.batch
  const taskSummary = data?.taskSummary

  const taskSortField = taskSortState?.[0]?.id ?? 'batchTaskId'
  const taskSortDir = taskSortState?.[0]?.desc ? 'desc' : 'asc'
  const { data: tasksData, isFetching: isTasksFetching } = useListBatchTasks(
    { batchId, status: taskFilter === 'all' ? undefined : taskFilter, take: taskPageState.pageSize, skip: taskPageState.pageIndex * taskPageState.pageSize, sortField: taskSortField, sortDir: taskSortDir },
    {
      enabled: !!batchId && (taskSummary?.total ?? 0) > 0,
      refetchInterval: () => batch?.isProcessing ? 10000 : false
    }
  )

  const handleSummaryClick = useCallback((filter) => {
    setTaskFilter(prev => prev === filter ? 'all' : filter)
    resetTaskPageState()
  }, [resetTaskPageState])

  const handleBack = useCallback(() => {
    history.push(ADMIN_ROUTES.STATUS_REPORTS.BATCHES.HOME)
  }, [history])

  if (isFetching && !data) {
    return (
      <div className='flex flex-col gap-sm p-5'>
        <div><SydButton variant='outline' size='sm' icon='arrowBack' onClick={handleBack}>Back to Batches</SydButton></div>
        <BatchDetailSkeleton metadataCount={4} />
      </div>
    )
  }

  if (!batch) {
    return (
      <div className='flex flex-col gap-lg p-5'>
        <div><SydButton variant='outline' size='sm' icon='arrowBack' onClick={handleBack}>Back to Batches</SydButton></div>
        <Card><div className='p-1'>Batch not found</div></Card>
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-sm p-5'>
      <div>
        <SydButton variant='outline' size='sm' icon='arrowBack' onClick={handleBack}>
          Back to Batches
        </SydButton>
      </div>
      <Card>
        <div className='flex flex-col gap-md p-md'>
          <div className='flex flex-row justify-between items-center'>
            <div className='flex flex-row gap-md items-center'>
              <div className='font-weight-bold font-size-xl'>
                Batch #{batchId}
              </div>
              <StatusBadge status={getBatchStatus(batch, taskSummary)} />
            </div>
            <div className='flex flex-row gap-sm'>
              {!!batch.isProcessing && (
                <SydButton
                  size='sm'
                  icon='sync'
                  variant='outline'
                  onClick={() => {
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.levelsBatchStatus] })
                    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.levelsBatchTasks] })
                  }}
                >
                  Refresh
                </SydButton>
              )}
            </div>
          </div>
          <div style={{ borderTop: '1px solid var(--color-neutral-200)' }} className='pt-3'>
            <div className='flex flex-row gap-xl'>
              <SydLabel label='Type'>
                <Text variant={TEXT_VARIANTS.body1} text='Status Report Run' />
              </SydLabel>
              <SydLabel label='As Of Date'>
                <Text variant={TEXT_VARIANTS.body1} text={batch.metadata?.asOfDate ? formatter(batch.metadata.asOfDate, '@MM/DD/YY') : '-'} />
              </SydLabel>
              <SydLabel label='Template ID'>
                <Text variant={TEXT_VARIANTS.body1} text={String(batch.metadata?.statusTemplateId || '-')} />
              </SydLabel>
              <SydLabel label='Started'>
                <Text variant={TEXT_VARIANTS.body1} text={batch.lastStartedDate ? formatter(batch.lastStartedDate, '&M/D/YY h:mm a') : '-'} />
              </SydLabel>
              {!!batch.lastFinishedDate && (
                <SydLabel label='Completed'>
                  <Text variant={TEXT_VARIANTS.body1} text={formatter(batch.lastFinishedDate, '&M/D/YY h:mm a')} />
                </SydLabel>
              )}
            </div>
          </div>
        </div>
      </Card>

      {taskSummary && (
        <SummaryCardGrid
          taskSummary={taskSummary}
          taskFilter={taskFilter}
          onFilterClick={handleSummaryClick}
        />
      )}

      {(taskSummary?.total ?? 0) > 0 && (
        <BatchTasksTable
          tasks={tasksData?.tasks ?? []}
          total={tasksData?.total ?? 0}
          loading={isTasksFetching}
          filter={taskFilter}
          pageState={taskPageState}
          sortState={taskSortState}
          onPageChange={setTaskPageState}
          onSortChange={setTaskSortState}
        />
      )}
    </div>
  )
}

export default StatusReportBatchDetail
